import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { Button } from 'reactstrap';
import PropTypes from 'prop-types';

import FormInput from '../../form/FormInput';
import FormSelect from '../../form/FormSelect';
import ConferenceCallDetail from './ConferenceCallDetail';
import { conferenceOptions, convertToMask } from '../../../utils/common';

import { useUserDetails } from '../../../store/slices/authSlice';
import {
  setConferenceDetail,
  setScreenType,
  useCallId,
  useCallNumber,
  useConferenceDetail,
  useCurrentCallDetail
} from '../../../store/slices/dialSlice';
import {
  addConference,
  hotKeyOptionGet,
  userOptionGetByProcessId
} from '../../../store/slices/callingSlice';

const ConferenceScreen = ({ number }) => {
  const user = useUserDetails();
  const callNumber = useCallNumber();
  const callId = useCallId();
  const CurrentCallDetail = useCurrentCallDetail();
  const conferenceDetail = useConferenceDetail();

  const dispatch = useDispatch();

  const [transferType, setTransferType] = useState('');
  const [transferValue, setTransferValue] = useState('');
  const [userId, setUserId] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [hotKeyOptions, setHotKeyOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const groupDataByRegister = (data) => {
    if (!data) return [];

    const createOptionsFromData = (filteredData) =>
      filteredData.map((item) => ({
        label: item.Username,
        value: item.id
      }));

    const registeredData = data.filter((item) => item.register === 'yes');
    const unregisteredData = data.filter((item) => item.register === 'no');

    return [
      {
        label: 'Registered',
        options: createOptionsFromData(registeredData)
      },
      {
        label: 'Unregistered',
        options: createOptionsFromData(unregisteredData)
      }
    ];
  };

  const fetchUserOptionByProcessId = async () => {
    try {
      const response = await dispatch(
        userOptionGetByProcessId({ process_id: user?.process_id })
      ).unwrap();
      setUserOptions(groupDataByRegister(response?.data));
    } catch (error) {
      console.error('Error fetching user options:', error);
    }
  };

  const fetchHotKeyOption = async () => {
    try {
      const response = await dispatch(hotKeyOptionGet(user?.process_id)).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.hot_key,
          value: val.hot_key_number
        }));
        setHotKeyOptions(options);
      } else {
        setHotKeyOptions([]);
      }
    } catch (error) {
      console.error('Error fetching user options:', error);
    }
  };

  useEffect(() => {
    if (user?.process_id) {
      fetchUserOptionByProcessId();
      fetchHotKeyOption();
    }
  }, [user?.process_id]);

  const handelAddConference = async () => {
    if (isLoading || !transferType) {
      return false;
    }
    if (!transferValue) {
      toast.error(
        transferType === 'external'
          ? 'please enter number'
          : transferType === 'internal'
            ? 'please select user'
            : 'please select hot key'
      );
      return false;
    }
    setIsLoading(true);
    try {
      const payload = {
        user_id: user?.user_id,
        origination_uuid: callId,
        process_id: user?.process_id,
        crm_id: CurrentCallDetail?.crm_id,
        crm_number: callNumber || number,
        transfer_type: transferType,
        transfer_value: transferValue,
        transfer_user_id: userId || undefined
      };
      const response = await dispatch(addConference(payload)).unwrap();
      if (response && response.success) {
        dispatch(setConferenceDetail(response?.conference));
        dispatch(setScreenType('CONFERENCE_LIVE'));
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  return (
    <div className="h-100 call-action-btn-container">
      {conferenceDetail && conferenceDetail?.details?.length > 0 ? (
        <ConferenceCallDetail isLoading={isLoading} number={number} />
      ) : (
        <div className="mt-auto mb-auto text-white text-center fw-bold fs-4">
          {user?.process_details?.numberMasking === 'true'
            ? convertToMask(callNumber || number)
            : callNumber || number}
          {isLoading ? <div className="text-lightGray fs-6">Connecting...</div> : null}
        </div>
      )}

      <div className="text-white mb-auto">
        <div className="mb-2">
          <FormSelect
            isShowLabel={false}
            name="transferType"
            placeholder="Select transfer type"
            value={transferType}
            options={conferenceOptions}
            onChange={(e) => {
              setTransferType(e?.target?.value);
              setUserId('');
              setTransferValue('');
            }}
          />
        </div>
        {transferType === 'internal' ? (
          <FormSelect
            isShowLabel={false}
            name="userId"
            placeholder="Select User"
            value={userId}
            options={userOptions}
            onChange={(e) => {
              setTransferValue(e?.target?.label);
              setUserId(e?.target?.value);
            }}
            isGroup
          />
        ) : transferType === 'external' ? (
          <FormInput
            isShowLabel={false}
            name="transferValue"
            placeholder="Enter number"
            value={transferValue}
            onChange={(e) => {
              const value = e.target.value;
              const val = value.replace(/[^0-9*#]+/g, '');
              setTransferValue(val);
            }}
          />
        ) : transferType === 'hot_key' ? (
          <FormSelect
            isShowLabel={false}
            name="transferValue"
            placeholder="Select hot key"
            value={transferValue}
            options={hotKeyOptions}
            onChange={(e) => {
              setTransferValue(e?.target?.value);
            }}
          />
        ) : null}
      </div>
      <div className="text-center conference-call-button-grid text-white mt-auto pb-4">
        <div className="d-flex justify-content-center">
          <Button
            className="back-button"
            onClick={() => {
              dispatch(
                setScreenType(
                  conferenceDetail && conferenceDetail?.details?.length > 0
                    ? 'CONFERENCE_LIVE'
                    : 'LIVE_CALL'
                )
              );
            }}>
            <Icon.ArrowLeft className="back-icon text-primary" />
          </Button>
        </div>
        <div className="d-flex justify-content-center">
          <Button className="back-button bg-call-color" onClick={() => handelAddConference()}>
            <Icon.PhoneCall className="back-icon text-white" />
          </Button>
        </div>
      </div>
    </div>
  );
};

ConferenceScreen.propTypes = {
  number: PropTypes.string
};

export default ConferenceScreen;
